<script lang="ts">
  import { adminModuleCss } from "../lib/gruimModuleTheme";
  import { onDestroy, onMount, tick } from "svelte";
  import Progressbar from "./progressbar.svelte";
  import MessageModal from "./messageModal.svelte";
  import { opportunityOptions } from "../lib/opportunityOptionsStore";
  import { syncOpportunityToHubspotById } from "../lib/hubspotAuth";
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";
  import type { OpportunityOptionsResponse } from "../lib/types";
  import { destroySvelteComponentInstance, mountFederatedSvelteComponent } from "../lib/mountFederatedSvelteComponent";

  const DEFAULT_URL = process.env.SVELTE_APP_REMOTE_URL;

  let el1: HTMLDivElement;
  let el2: HTMLDivElement;
  let el3: HTMLDivElement;
  let marketingActivitiesInstance: any = null;
  let marketingChannelsInstance: any = null;
  let marketingUseCasesInstance: any = null;

  let total = 0;
  let completed = 0;
  let showProgressBar = false;

  let showSyncModal = false;
  let syncMessage: string = "";
  let syncDetails: Record<string, unknown> | null = null;

  const closeModal = (): void => {
    showSyncModal = false;
    syncMessage = "";
    syncDetails = null;
  };

  let showChannels = false;
  let showUseCases = false;
  let togglingChannels = false;
  let togglingUseCases = false;

  let opts: OpportunityOptionsResponse | null = null;

  const toggleChannels = async () => {
    togglingChannels = true;
    showChannels = !showChannels;
    await tick();

    if (showChannels && el2) {
      const { default: MarketingChannels } = await import(
        "App/alaceMarketingChannels"
      );
      marketingChannelsInstance = mountFederatedSvelteComponent(
        marketingChannelsInstance,
        MarketingChannels,
        {
          target: el2,
          props: {
          css: adminModuleCss,
          translation: { channel: "Channel" },
          schema: {
            actions: { edit: { removable: true, "with-confirmation": true } },
            "field-properties": {
              "auto-generated-fields": ["id", "marketingActivityId"],
              "field-order": ["channel"],
              "dropdown-fields": [
                {
                  name: "channel",
                  options: opts.marketingChannel.map((v) => ({ label: v, value: v })),
                },
              ],
            },
          },
          },
        }
      );
    } else {
      marketingChannelsInstance = destroySvelteComponentInstance(marketingChannelsInstance);
    }

    togglingChannels = false;
  };

  const toggleUseCases = async () => {
    togglingUseCases = true;
    showUseCases = !showUseCases;
    await tick();

    if (showUseCases && el3) {
      const { default: MarketingUseCases } = await import(
        "App/alaceMarketingUseCases"
      );
      marketingUseCasesInstance = mountFederatedSvelteComponent(
        marketingUseCasesInstance,
        MarketingUseCases,
        {
          target: el3,
          props: {
          css: adminModuleCss,
          translation: { useCase: "Use Case" },
          schema: {
            actions: { edit: { removable: true, "with-confirmation": true } },
            "field-properties": {
              "auto-generated-fields": ["id", "marketingActivityId"],
              "field-order": ["useCase"],
              "dropdown-fields": [
                {
                  name: "useCase",
                  options: opts.marketingUseCase.map((v) => ({ label: v, value: v })),
                },
              ],
            },
          },
          },
        }
      );
    } else {
      marketingUseCasesInstance = destroySvelteComponentInstance(marketingUseCasesInstance);
    }

    togglingUseCases = false;
  };

  const mountMarketing = async () => {
    const { default: MarketingActivities } = await import(
      "App/alaceMarketingActivities"
    );

    if (el1) {
      marketingActivitiesInstance = mountFederatedSvelteComponent(
        marketingActivitiesInstance,
        MarketingActivities,
        {
          target: el1,
          props: {
          css: adminModuleCss,
          translation: {
            wasMarketingFundsUsed: "AWS Funds Used",
            isFromMarketingActivity: "Marketing Source",
            marketingCampaign: "Campaign",
            channels: "Channels",
            useCases: "Use Cases",
            channel: "Channel",
            useCase: "Use Case",
          },
          schema: {
            actions: {
              edit: { removable: true, "with-confirmation": true },
            },
            "field-properties": {
              "auto-generated-fields": ["id", "opportunityId"],
              "field-order": [
                "wasMarketingFundsUsed",
                "isFromMarketingActivity",
                "marketingCampaign",
              ],
              "relational-fields": [
                {
                  name: "channels",
                  type: "DEFAULT",
                  editable: true,
                  columns: ["channel"],
                },
                {
                  name: "useCases",
                  type: "DEFAULT",
                  editable: true,
                  columns: ["useCase"],
                },
              ],
              "dropdown-fields": [
                {
                  name: "wasMarketingFundsUsed",
                  options: [
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ],
                },
                {
                  name: "isFromMarketingActivity",
                  options: opts?.marketingSource?.map((v) => ({ label: v, value: v })) ?? [],
                },
              ],
            },
            "custom-bulk-actions": [
              {
                action: "Sync to AWS",
                bulkFn: async (ids: number[]) => {
                  showProgressBar = true;
                  total = ids.length;
                  completed = 0;

                  const results: any[] = [];

                  for (const marketingId of ids) {
                    try {
                      const res = await fetch(
                        `${DEFAULT_URL}/api/marketing-activities/${marketingId}`
                      );
                      const marketing = await res.json();

                      if (!marketing?.opportunityId) {
                        results.push({
                          success: false,
                          message: `No opportunity linked to marketing activity ${marketingId}`,
                        });
                        completed++;
                        continue;
                      }

                      const syncRes = await fetch(
                        `${DEFAULT_URL}/api/alace/sync/opportunity/${marketing.opportunityId}`,
                        { method: "POST" }
                      );

                      const syncResult = await syncRes.json();
                      results.push({
                        success: true,
                        marketingId,
                        opportunityId: marketing.opportunityId,
                        synced: syncResult,
                      });
                    } catch (err: any) {
                      console.error(`Sync failed for marketingId ${marketingId}:`, err);
                      results.push({
                        success: false,
                        marketingId,
                        message: err.message || "Unknown error",
                      });
                    }

                    completed++;
                  }

                  showProgressBar = false;
                  if (results.some((result) => result?.success)) {
                    triggerOpportunityDataRefresh();
                  }
                  return results;
                },
                singleFn: async (row: { id: number; opportunity?: number }) => {
                  if (!row.opportunity) {
                    syncMessage = "No opportunity linked to this marketing activity";
                    syncDetails = {};
                    showSyncModal = true;
                    return {
                      success: false,
                      message: syncMessage,
                    };
                  }

                  try {
                    const res = await fetch(
                      `${DEFAULT_URL}/api/alace/sync/opportunity/${row.opportunity}`,
                      { method: "POST" }
                    );

                    const result = await res.json();

                    syncMessage = "Marketing sync completed.";
                    syncDetails = result;
                    showSyncModal = true;
                    if (result?.success !== false) {
                      triggerOpportunityDataRefresh();
                    }

                    return { success: true, synced: [result] };
                  } catch (err: any) {
                    console.error("Sync failed:", err);
                    syncMessage = "Sync failed due to an error.";
                    syncDetails = { error: err.message };
                    showSyncModal = true;
                    return { success: false, message: syncMessage };
                  }
                },
              },
              {
                action: "Sync to HubSpot",
                bulkFn: async (ids: number[]) => {
                  showProgressBar = true;
                  total = ids.length;
                  completed = 0;

                  const results: any[] = [];

                  for (const marketingId of ids) {
                    try {
                      const res = await fetch(
                        `${DEFAULT_URL}/api/alace/marketing-activities/${marketingId}`
                      );
                      const marketing = await res.json();

                      if (!marketing?.opportunityId) {
                        results.push({
                          success: false,
                          message: `No opportunity linked to marketing activity ${marketingId}`,
                        });
                        completed++;
                        continue;
                      }

                      const syncResult = await syncOpportunityToHubspotById(
                        marketing.opportunityId
                      );
                      results.push({
                        success: syncResult.success,
                        marketingId,
                        opportunityId: marketing.opportunityId,
                        synced: syncResult,
                        authRequired: syncResult.authRequired,
                        message: syncResult.message,
                      });

                      if (syncResult.authRequired) {
                        showProgressBar = false;
                        return results;
                      }
                    } catch (err: any) {
                      console.error(`HubSpot sync failed for marketingId ${marketingId}:`, err);
                      results.push({
                        success: false,
                        marketingId,
                        message: err?.message || "Unknown error",
                      });
                    }

                    completed++;
                  }

                  showProgressBar = false;
                  if (results.some((result) => result?.success)) {
                    triggerOpportunityDataRefresh();
                  }
                  return results;
                },
                singleFn: async (row: { id: number; opportunity?: number }) => {
                  if (!row.opportunity) {
                    syncMessage = "No opportunity linked to this marketing activity";
                    syncDetails = {};
                    showSyncModal = true;
                    return { success: false, message: syncMessage };
                  }

                  const result = await syncOpportunityToHubspotById(row.opportunity, {
                    fallbackMessage: "Failed to sync the marketing opportunity to HubSpot.",
                  });

                  if (result.authRequired) {
                    return result;
                  }

                  syncMessage = result.success
                    ? "Marketing sync to HubSpot completed."
                    : result.message || "Failed to sync the marketing opportunity to HubSpot.";
                  syncDetails = result;
                  showSyncModal = true;
                  if (result.success) {
                    triggerOpportunityDataRefresh();
                  }

                  return { success: result.success, synced: [result] };
                },
              },
            ],
          },
          },
        }
      );
    }
  };

  onMount(async () => {
    opts = await opportunityOptions.fetchOptions();
    void mountMarketing();
  });

  onDestroy(() => {
    marketingActivitiesInstance = destroySvelteComponentInstance(marketingActivitiesInstance);
    marketingChannelsInstance = destroySvelteComponentInstance(marketingChannelsInstance);
    marketingUseCasesInstance = destroySvelteComponentInstance(marketingUseCasesInstance);
  });
</script>

{#if showProgressBar}
  <Progressbar {completed} {total} />
{/if}

<div bind:this={el1} />

<button
  on:click={toggleChannels}
  class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mt-3 disabled:opacity-50"
  disabled={togglingChannels}
>
  {togglingChannels ? "Loading..." : showChannels ? "Hide Channels" : "Show Channels"}
</button>
{#if showChannels}
  <div bind:this={el2} />
{/if}

<button
  on:click={toggleUseCases}
  class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mt-3 disabled:opacity-50"
  disabled={togglingUseCases}
>
  {togglingUseCases ? "Loading..." : showUseCases ? "Hide Use Cases" : "Show Use Cases"}
</button>
{#if showUseCases}
  <div bind:this={el3} />
{/if}

<MessageModal
  show={showSyncModal}
  title="Sync Result"
  message={syncMessage}
  details={syncDetails}
  onClose={closeModal}
/>
